/*
 * Switching a card's option value in place.
 *
 * The plates were links to the product page: picking "Blue" to see its price
 * meant a full page load, and coming back meant another. Everything needed to
 * answer that question is already in the markup -- item.liquid writes each
 * value's price, compare-at price and featured media onto its own plate -- so
 * the card can answer it itself.
 *
 * The links stay real. This intercepts the click; with scripting off, or if
 * anything here throws, the href still opens the right variant.
 *
 * Delegated from the document, like item-media-nav: collection pages append
 * cards as you scroll, and anything bound at load would leave later pages dead.
 */
(function () {
  'use strict';

  function cardOf(node) {
    return node.closest ? node.closest('.item') : null;
  }

  /* The media strip is keyed by Shopify media id where the theme knows it.
     A variant with no featured media leaves the picture alone rather than
     guessing -- showing the wrong photograph is worse than showing a general
     one. */
  function showMedia(card, mediaId) {
    if (!mediaId) return;
    var strip = card.querySelector('[data-item-strip]');
    if (!strip) return;

    // The variant's picture may be one of the slides held in the card's
    // <template> (item.liquid) rather than in the strip yet. A hover or touch
    // normally brings them in first, but a script-driven click would not.
    var more = strip.querySelector('template[data-item-more]');
    if (more) more.replaceWith(more.content);

    var slides = strip.querySelectorAll('.item__slide');
    for (var i = 0; i < slides.length; i++) {
      if (slides[i].getAttribute('data-media-id') === String(mediaId)) {
        strip.scrollTo({ left: slides[i].offsetLeft - strip.offsetLeft, behavior: 'smooth' });
        return;
      }
    }
  }

  /* The card's cart button follows the plate too. It was set once at render,
     so after tapping "Big" the photo and price said Big while the button
     still added Small and its confirmation still said "You've selected
     Small". Every plate is an in-stock variant, so the input can always be
     enabled. A card with no cart button, or one whose markup differs, is
     left exactly as it was. */
  function syncQuickAdd(card, pill) {
    var component = card.querySelector('quick-add-component');
    if (!component) return;

    var variantId = pill.getAttribute('data-item-variant');
    var input = component.querySelector('input[name="id"]');
    if (input && variantId) {
      input.value = variantId;
      input.disabled = false;
    }

    // Where the "Choose" modal and "View product details" go.
    var href = pill.getAttribute('href');
    if (href) component.dataset.productUrl = href;

    // The confirmation's wording -- only one-option products carry it.
    if (component.dataset.variantSelected === undefined) return;
    var selected = pill.textContent.trim();
    component.dataset.variantSelected = selected;

    var inStock = [];
    try {
      inStock = JSON.parse(component.dataset.variantInstock || '[]');
    } catch (e) {
      inStock = [];
    }
    component.dataset.variantOthers = inStock
      .filter(function (value) {
        return value !== selected;
      })
      .join(', ');
  }

  function swap(pill) {
    var card = cardOf(pill);
    if (!card) return false;

    var price = card.querySelector('.item__meta-now');
    if (!price) return false;

    var next = pill.getAttribute('data-variant-price');
    if (!next) return false;

    price.textContent = next;

    // The struck-through price belongs to this variant or to no variant; a
    // stale one left standing claims a discount that is not on offer.
    var was = card.querySelector('.item__meta-was');
    var compare = pill.getAttribute('data-variant-compare');
    if (was) was.textContent = compare || '';
    if (was) was.hidden = !compare;

    var siblings = pill.parentElement.querySelectorAll('.item__option');
    for (var i = 0; i < siblings.length; i++) {
      siblings[i].classList.remove('item__option--current');
      siblings[i].removeAttribute('aria-current');
    }
    pill.classList.add('item__option--current');
    pill.setAttribute('aria-current', 'true');

    // The card's own link follows the choice, or the picture and the
    // destination stop agreeing.
    var link = card.querySelector('.item__link');
    if (link) link.setAttribute('href', pill.getAttribute('href'));

    showMedia(card, pill.getAttribute('data-variant-media'));
    syncQuickAdd(card, pill);
    return true;
  }

  /* ---- the dropdown ----------------------------------------------------
     The row on a card is a pill showing the current value and a "+N sizes"
     label (item.liquid). The list of values is held in a <template> and
     copied out the first time the pill is tapped, so a card costs a handful
     of elements until someone asks. The list is a popover: the browser puts
     it in the top layer, so no card, carousel or overflow can clip it, and it
     is placed from the pill's position when it opens. */
  var canPopover = typeof HTMLElement !== 'undefined' && typeof HTMLElement.prototype.showPopover === 'function';
  var openRow = null;

  function menuOf(row) {
    var menu = row.querySelector('.item__menu');
    if (menu) return menu;
    var template = row.querySelector('template[data-item-select-list]');
    if (!template) return null;
    template.replaceWith(template.content);
    return row.querySelector('.item__menu');
  }

  function closeMenu(returnFocus) {
    if (!openRow) return;
    var row = openRow;
    openRow = null;
    var menu = row.querySelector('.item__menu');
    var trigger = row.querySelector('.item__select');
    if (menu && menu.matches(':popover-open')) menu.hidePopover();
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
      if (returnFocus) trigger.focus({ preventScroll: true });
    }
    window.removeEventListener('scroll', onScroll, true);
    window.removeEventListener('resize', onResize);
  }

  // A fixed list would be left hanging if the card moved, so any scroll
  // outside the list itself closes it.
  function onScroll(event) {
    if (event.target && event.target.closest && event.target.closest('.item__menu')) return;
    closeMenu(false);
  }

  function onResize() {
    closeMenu(false);
  }

  function openMenu(row) {
    var trigger = row.querySelector('.item__select');
    var menu = menuOf(row);
    if (!trigger || !menu) return;

    menu.classList.toggle('item__menu--scroll', menu.querySelectorAll('.item__option').length > 8);
    menu.showPopover();
    trigger.setAttribute('aria-expanded', 'true');

    var card = row.closest('.item') || row;
    var t = trigger.getBoundingClientRect();
    var c = card.getBoundingClientRect();
    var width = Math.max(150, Math.min(c.width - 16, 320));
    var left = Math.max(8, Math.min(c.left + (c.width - width) / 2, window.innerWidth - width - 8));
    menu.style.width = width + 'px';

    // Under the pill; above it when there is no room below.
    var height = menu.offsetHeight;
    var below = t.bottom + 8;
    var above = t.top - height - 8;
    var flip = below + height > window.innerHeight - 8 && above >= 8;
    menu.classList.toggle('item__menu--above', flip);
    menu.style.left = left + 'px';
    menu.style.top = (flip ? above : below) + 'px';
    menu.style.setProperty('--caret-x', Math.round(Math.max(18, Math.min(t.left + t.width / 2 - left, width - 18))) + 'px');

    openRow = row;
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);

    var current = menu.querySelector('.item__option--current') || menu.querySelector('.item__option');
    if (current) current.focus({ preventScroll: true });
  }

  // After a choice: the pill shows the new value and the list marks it.
  function syncSelect(menu, pill) {
    var row = menu.closest('[data-item-select]');
    var trigger = row && row.querySelector('.item__select');
    var name = pill.textContent.trim();
    if (trigger) {
      trigger.textContent = name;
      var label = trigger.getAttribute('aria-label') || '';
      var colon = label.indexOf(':');
      trigger.setAttribute('aria-label', (colon > -1 ? label.slice(0, colon) : '') + ': ' + name);
    }
    var items = menu.querySelectorAll('.item__option');
    for (var i = 0; i < items.length; i++) {
      items[i].setAttribute('aria-selected', items[i] === pill ? 'true' : 'false');
    }
  }

  document.addEventListener('click', function (event) {
    var target = event.target;

    // A value chosen from the open list. Choosing the one already showing just
    // closes it. If the swap cannot happen the link is left to do what it
    // always did (open that variant on the product page).
    var pill = target.closest('[data-item-variant]');
    if (pill) {
      var menu = pill.closest('.item__menu');
      if (pill.classList.contains('item__option--current') || swap(pill)) {
        event.preventDefault();
        event.stopPropagation();
        if (menu) syncSelect(menu, pill);
        closeMenu(true);
      }
      return;
    }

    // The pill or its "+N sizes" label: open the list, or close it.
    var row = target.closest('[data-item-select]');
    if (row && !target.closest('.item__menu') && !target.closest('.item__select--static')) {
      event.preventDefault();
      event.stopPropagation();
      if (!canPopover) {
        // No popover support: send them to the product, where every value is.
        var link = (row.closest('.item') || row).querySelector('.item__link');
        if (link) link.click();
        return;
      }
      var wasOpen = openRow === row;
      closeMenu(false);
      if (!wasOpen) openMenu(row);
      return;
    }

    // Anywhere else.
    if (openRow && !target.closest('.item__menu')) closeMenu(false);
  });

  document.addEventListener('keydown', function (event) {
    if (!openRow) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeMenu(true);
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      var menu = openRow.querySelector('.item__menu');
      if (!menu) return;
      var items = Array.prototype.slice.call(menu.querySelectorAll('.item__option'));
      var at = items.indexOf(document.activeElement);
      var next = event.key === 'ArrowDown' ? Math.min(items.length - 1, at + 1) : Math.max(0, at - 1);
      if (items[next]) {
        event.preventDefault();
        items[next].focus({ preventScroll: true });
      }
    }
  });
})();
